// File: /src/components/settings/Settings.js
import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  BarChart3,
  Bluetooth,
  Code2,
  Cpu,
  HandCoins,
  History,
  Hourglass,
  Link2,
  Medal,
  MonitorSmartphone,
  Smartphone,
  Sparkles,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useAppState, WorkoutContext } from "../../contexts/WorkoutContext";
import api from "../../services/api";
import useWorkoutSettings from "../../hooks/useWorkoutSettings";
import useAudioSettings from "../../hooks/useAudioSettings";
import { SOUND_PROFILES } from "../../config/soundProfiles";
import { LLM_PROVIDERS, getModelDisplayName } from "../../config/llmProviders";
import DetailSheet from "../session/generate/DetailSheet";
import { AddRow, Group, ItemRow, MoreOptionsRow, SummaryRow } from "../profile/ProfileParts";
import AddDeviceSheet from "./AddDeviceSheet";
import { useShowAdvanced } from "../../hooks/useUiMode";
import NexusSheet, { nexusStatus, useNexusLink } from "../integrations/NexusSheet";
import AiEngineSheet from "./AiEngineSheet";
import CreditSharesSheet, { describeCreditShares, useCreditShares } from "./CreditSharesSheet";
import UsageSheet, { useUsageTotal } from "./UsageSheet";
import { displayCurrency } from "../../utils/money";
import { isOnShare, settingsForShare, shareTitle } from "../../utils/creditShares";
import BluetoothSheet, { BluetoothRow, ReconnectFlagsTip, useBluetoothItems } from "./BluetoothSheet";
import DebugSettings from "./DebugSettings";
import SettingsSheet from "./SettingsSheet";
import SoundsSheet from "./SoundsSheet";
import TimerSheet, { LinkTimerSheet } from "./TimerSheet";
import { SharedTimerSheet } from "./TimerSharing";
import { isInstallQueued, isUpdateAvailable } from "./TimerFirmware";
import useKhronosTimers from "./useKhronosTimers";
import {
  describeBadges,
  describeCoachScreen,
  describeCountdowns,
  describeOfferPairing,
  describePhone,
  describeRests,
  describeSounds,
  formatLastSeen,
} from "./settingsSummaries";

const SIMPLE_SHEETS = ["rests", "coachScreen", "countdowns", "phone", "offer", "badges"];

const Dot = ({ on }) => (
  <span
    className={`mr-1.5 inline-block h-[7px] w-[7px] rounded-full align-[1px] ${
      on ? "bg-green-500" : "bg-muted-foreground/60"
    }`}
  />
);

const UpdateTag = ({ children }) => (
  <span className="whitespace-nowrap rounded-full bg-yellow-500/15 px-2 py-0.5 text-[11px] font-semibold text-yellow-500">
    {children}
  </span>
);

/**
 * Settings (issue #86): one page of grouped rows, like Profile. Every row
 * opens a sheet; switches that answer the same question share a sheet, so no
 * row is a lone switch. Settings is how the app behaves, in the order you meet
 * it: the Coach, sound and phone, your devices, the AI, then mates and apps.
 * Profile is what shapes your sessions; anything you'd set together sits
 * together on one of the two, never split across both.
 */
const Settings = () => {
  const { settingsSheet, setSettingsSheet } = useAppState();
  const { llmSettings, setLlmSettings, money } = useContext(WorkoutContext);
  const { isAuthenticated } = useAuth();
  const credits = useCreditShares(isAuthenticated);
  const usage = useUsageTotal(isAuthenticated, displayCurrency(money));

  const syncToApi = useCallback(
    (apiFields) => {
      if (!isAuthenticated) return;
      api.updateSettings(apiFields).catch(console.error);
    },
    [isAuthenticated],
  );
  const [workoutSettings, updateWorkoutSettings] = useWorkoutSettings({ onSync: syncToApi });
  const [audio, updateAudio] = useAudioSettings({ onSync: syncToApi });
  const timers = useKhronosTimers(isAuthenticated);
  const { eq, items: btItems } = useBluetoothItems();
  const nexus = useNexusLink(isAuthenticated);

  // Which sheet is open: a kind string, or { timer } / { ble } with an id.
  const [sheet, setSheet] = useState(null);
  const close = () => setSheet(null);

  // A deep link (Generate's "AI settings") opens its sheet once, on arrival.
  useEffect(() => {
    if (!settingsSheet) return;
    setSheet(settingsSheet);
    setSettingsSheet(null);
  }, [settingsSheet, setSettingsSheet]);

  const ws = workoutSettings;
  // Basic mode (#172) tucks the fine-tuning rows away; their settings keep
  // working at whatever they're set to.
  const { show: showAll, advanced, reveal } = useShowAdvanced();
  const provider = LLM_PROVIDERS[llmSettings.provider];
  const soundSet = SOUND_PROFILES[audio.profile]?.label || audio.profile;
  const hasRower = eq.devices.some((d) => d.type === "rower");

  const coach = (
    <Group title="Coach" note="How the timer plays a session">
      <SummaryRow Icon={MonitorSmartphone} label="Coach screen" value={describeCoachScreen(ws)} onClick={() => setSheet("coachScreen")} />
      {showAll && (
        <SummaryRow Icon={Hourglass} label="Countdowns" value={describeCountdowns(audio)} onClick={() => setSheet("countdowns")} />
      )}
      {showAll && (
        <SummaryRow Icon={History} label="Rests & interruptions" value={describeRests(ws)} onClick={() => setSheet("rests")} />
      )}
    </Group>
  );

  const soundAndPhone = (
    <Group title="Sound & phone">
      <SummaryRow
        Icon={audio.muted ? VolumeX : Volume2}
        label="Sounds"
        value={describeSounds(audio, soundSet)}
        muted={audio.muted}
        onClick={() => setSheet("sounds")}
      />
      <SummaryRow
        Icon={Smartphone}
        label="Phone"
        value={describePhone(audio, ws)}
        muted={!audio.hapticEnabled && !ws.wakeLockEnabled && !ws.weatherOnSessions}
        onClick={() => setSheet("phone")}
      />
    </Group>
  );

  const ai = (
    <Group title="AI engine" note="Writes your sessions">
      <SummaryRow
        Icon={Sparkles}
        label="AI"
        value={`${isOnShare(llmSettings) ? shareTitle(llmSettings.creditShare) : provider?.label || llmSettings.provider} · ${getModelDisplayName(llmSettings.provider, llmSettings.model, money)}`}
        onClick={() => setSheet("ai")}
      />
      {isAuthenticated && showAll && (
        <SummaryRow
          Icon={BarChart3}
          label="Usage"
          value={usage.total ? `${usage.total} this month` : ""}
          onClick={() => setSheet("usage")}
        />
      )}
      {isAuthenticated && (
        <SummaryRow
          Icon={HandCoins}
          label="Shared credits"
          value={credits.loading ? "" : describeCreditShares(credits)}
          muted={describeCreditShares(credits) === "Off"}
          onClick={() => setSheet("credits")}
        />
      )}
    </Group>
  );

  const timerRows = timers.devices.map((device) => {
    const online = timers.online[device.id] === true;
    const tag = isInstallQueued(device) ? (
      <UpdateTag>Queued</UpdateTag>
    ) : isUpdateAvailable(device, timers.latestFor(device)) ? (
      <UpdateTag>Update</UpdateTag>
    ) : null;
    return (
      <ItemRow
        key={device.id}
        Icon={Cpu}
        title={device.name}
        detail={
          <>
            <Dot on={online} />
            {device.in_use_by
              ? `In use by ${device.in_use_by}`
              : online
                ? "Online"
                : `Last seen ${formatLastSeen(device.last_seen_at).toLowerCase()}`}
            {device.firmware_version ? ` · v${device.firmware_version}` : ""}
          </>
        }
        tag={tag}
        onClick={() => setSheet({ timer: device.id })}
      />
    );
  });

  // Timers mates have shared with you (#171).
  const sharedRows = timers.shared.map((device) => {
    const online = timers.online[device.id] === true;
    return (
      <ItemRow
        key={`shared-${device.id}`}
        Icon={Cpu}
        title={device.name}
        detail={
          <>
            <Dot on={online} />
            {device.in_use_by ? `In use by ${device.in_use_by}` : online ? "Online" : "Offline"}
            {` · ${device.owner_name}'s`}
          </>
        }
        onClick={() => setSheet({ sharedTimer: device.id })}
      />
    );
  });

  const bluetoothNote = !eq.isSupported
    ? "Pairing Bluetooth gear needs Chrome or Edge on Android or a computer."
    : [
        eq.pairError,
        eq.hrPairError,
        !hasRower &&
          "Can't see your Concept2 rower? Update the PM5 to firmware 22 or newer with the Concept2 Utility.",
      ]
        .filter(Boolean)
        .join(" ");
  const timerNote = !isAuthenticated
    ? "Sign in to link a Khronos Timer, the wall display that runs your sessions."
    : timers.error ||
      (timers.devices.length === 0 && timers.shared.length === 0 && !timers.loading
        ? "The Khronos Timer is the wall display. Link it to send sessions to it and control it from here."
        : null);
  const devicesNote = [timerNote, bluetoothNote].filter(Boolean).join(" ");
  const devicesFooter = eq.isSupported && eq.autoReconnectBlocked ? (
    <div className="mt-1 grid gap-2">
      {devicesNote && <span>{devicesNote}</span>}
      <ReconnectFlagsTip />
    </div>
  ) : (
    devicesNote
  );

  // Devices: your Khronos Timers and Bluetooth gear in one list, with one Add.
  const devices = (
    <Group title="Devices" note="Your timer and Bluetooth gear" footer={devicesFooter}>
      {isAuthenticated && timers.loading && timers.devices.length === 0 ? (
        <p className="px-3 py-3 text-sm text-muted-foreground">Loading your timers…</p>
      ) : (
        <>
          {timerRows}
          {sharedRows}
        </>
      )}
      {eq.isSupported &&
        btItems.map((item) => <BluetoothRow key={item.key} item={item} onClick={() => setSheet({ ble: item.key })} />)}
      <AddRow
        label={eq.pairing || eq.hrPairing ? "Pairing…" : "Add a device"}
        onClick={() => !eq.pairing && !eq.hrPairing && setSheet("addDevice")}
      />
      <SummaryRow
        Icon={Bluetooth}
        label="In workouts"
        value={describeOfferPairing(ws)}
        muted={ws.showConnectTrainer === false}
        onClick={() => setSheet("offer")}
      />
    </Group>
  );

  const nexusState = nexusStatus(nexus.link);
  const matesAndApps = (
    <Group title="Mates & apps">
      <SummaryRow
        Icon={Medal}
        label="Badges"
        value={describeBadges(ws)}
        muted={ws.showBadges === false}
        onClick={() => setSheet("badges")}
      />
      {isAuthenticated && showAll && (
        <ItemRow
          Icon={Link2}
          title="Nexus"
          detail={nexus.loading ? "Checking…" : nexusState ? nexusState.label : "Not linked"}
          onClick={() => setSheet("nexus")}
        />
      )}
    </Group>
  );

  return (
    <div className="mx-auto grid w-full max-w-5xl grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-2 lg:gap-x-8">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-6">
        {coach}
        {soundAndPhone}
        {devices}
      </div>
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-6">
        {ai}
        {matesAndApps}
        {showAll && (
          <Group title="Troubleshooting">
            <SummaryRow Icon={Code2} label="Developer tools" value="" muted onClick={() => setSheet("devtools")} />
          </Group>
        )}
        {!showAll && <MoreOptionsRow onClick={reveal} />}
        {!advanced && showAll && (
          <p className="px-0.5 text-xs leading-snug text-muted-foreground">
            Showing every option for now. To always see them, turn on Advanced in the menu under your profile picture.
          </p>
        )}
      </div>

      <SettingsSheet
        kind={SIMPLE_SHEETS.includes(sheet) ? sheet : null}
        onClose={close}
        workoutSettings={ws}
        updateWorkoutSettings={updateWorkoutSettings}
        audio={audio}
        updateAudio={updateAudio}
      />
      <SoundsSheet
        open={sheet === "sounds"}
        onClose={close}
        audio={audio}
        updateAudio={updateAudio}
        hasTimer={timers.devices.length > 0}
      />
      <AiEngineSheet open={sheet === "ai"} onClose={close} credits={isAuthenticated ? credits : null} />
      <UsageSheet
        open={sheet === "usage"}
        onClose={() => {
          close();
          usage.reload();
        }}
      />
      <CreditSharesSheet
        open={sheet === "credits"}
        credits={credits}
        onClose={close}
        onUseShare={(share) => setLlmSettings(settingsForShare(llmSettings, share))}
      />
      <TimerSheet deviceId={sheet?.timer ?? null} timers={timers} onClose={close} />
      <SharedTimerSheet deviceId={sheet?.sharedTimer ?? null} timers={timers} onClose={close} />
      <LinkTimerSheet open={sheet === "link"} timers={timers} onClose={close} />
      <AddDeviceSheet
        open={sheet === "addDevice"}
        onClose={close}
        canLinkTimer={isAuthenticated}
        bluetooth={eq.isSupported}
        onLinkTimer={() => setSheet("link")}
        onPairEquipment={() => {
          close();
          eq.addDevice();
        }}
        onPairStrap={() => {
          close();
          eq.addHrMonitor();
        }}
      />
      <BluetoothSheet itemKey={sheet?.ble ?? null} onClose={close} />
      <NexusSheet open={sheet === "nexus"} nexus={nexus} onClose={close} />
      <DetailSheet open={sheet === "devtools"} onOpenChange={(o) => !o && close()} title="Developer tools">
        <DebugSettings />
      </DetailSheet>
    </div>
  );
};

export default Settings;
